import { useState, useEffect } from 'react'

export default function LexisReport() {
    const [report, setReport] = useState(null)
    const [lang, setLang] = useState('en')
    const [loading, setLoading] = useState(true)
    const [regenerating, setRegenerating] = useState(false)

    useEffect(() => { load() }, [lang])
    const load = async () => {
        setLoading(true)
        try {
            const res = await fetch(`/api/lexis/portfolio-report?lang=${lang}`).then(r => r.json())
            setReport(res.report || res)
        } catch (e) { console.error(e) }
        setLoading(false)
    }

    const regenerate = async () => {
        setRegenerating(true)
        try {
            await fetch('/api/lexis/portfolio-report/regenerate', { method: 'POST' })
            load()
        } catch (e) { console.error(e) }
        setRegenerating(false)
    }

    if (loading) return (
        <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} style={{ background: 'rgba(30,41,59,0.4)', borderRadius: '12px', padding: '24px', height: '100px', animation: 'pulse 1.5s infinite' }} />
                ))}
            </div>
        </div>
    )

    const Skeleton = () => <div style={{ background: 'rgba(30,41,59,0.4)', borderRadius: '12px', padding: '20px', animation: 'pulse 1.5s infinite' }} />

    return (
        <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                    <h2 style={{ margin: 0, color: '#f1f5f9' }}>📊 Portfolio Intelligence</h2>
                    <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '13px' }}>Phân Tích Danh Mục</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => setLang(lang === 'en' ? 'vi' : 'en')} style={{
                        padding: '8px 14px', background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)',
                        borderRadius: '6px', color: '#d4af37', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                    }}>{lang === 'en' ? '🇻🇳 VI' : '🇺🇸 EN'}</button>
                    <button onClick={regenerate} disabled={regenerating} style={{
                        padding: '8px 14px', background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(148,163,184,0.2)',
                        borderRadius: '6px', color: '#94a3b8', cursor: regenerating ? 'not-allowed' : 'pointer', fontSize: '12px',
                        opacity: regenerating ? 0.6 : 1,
                    }}>🔄 Regenerate</button>
                </div>
            </div>

            {regenerating && <p style={{ color: '#d4af37', fontSize: '13px' }}>Regenerating report...</p>}
            {report?.generated_at && (
                <p style={{ color: '#64748b', fontSize: '12px', marginBottom: '20px' }}>Last generated: {new Date(report.generated_at).toLocaleString()}</p>
            )}

            {/* Section 1: Coverage Map */}
            <div style={{
                background: 'rgba(30, 41, 59, 0.6)', borderRadius: '12px', padding: '24px',
                marginBottom: '20px', border: '1px solid rgba(148,163,184,0.1)',
            }}>
                <h3 style={{ color: '#d4af37', margin: '0 0 16px', fontSize: '15px' }}>📊 Solution Coverage Map</h3>
                {report?.coverage?.map((item, i) => {
                    const badgeColor = item.coverage === 'full' ? '#22c55e' : item.coverage === 'partial' ? '#f59e0b' : '#ef4444'
                    const badgeText = item.coverage === 'full' ? 'Full' : item.coverage === 'partial' ? 'Partial' : 'None'
                    return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < report.coverage.length - 1 ? '1px solid rgba(148,163,184,0.1)' : 'none' }}>
                            <div>
                                <span style={{ color: '#e2e8f0', fontWeight: 600 }}>{item.solution_type}</span>
                                <p style={{ color: '#64748b', fontSize: '12px', margin: '4px 0 0' }}>Products: {item.products_count}</p>
                            </div>
                            <span style={{
                                background: `${badgeColor}20`, color: badgeColor,
                                padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 600,
                            }}>{badgeText}</span>
                        </div>
                    )
                })}
            </div>

            {/* Section 2: Recommendations */}
            {report?.recommendations?.length > 0 && (
                <div style={{
                    background: 'rgba(30, 41, 59, 0.6)', borderRadius: '12px', padding: '24px',
                    marginBottom: '20px', border: '1px solid rgba(212,175,55,0.2)',
                }}>
                    <h3 style={{ color: '#d4af37', margin: '0 0 16px', fontSize: '15px' }}>💡 Product Recommendations</h3>
                    {report.recommendations.map((r, i) => (
                        <div key={i} style={{
                            background: 'rgba(212,175,55,0.05)', borderRadius: '10px', padding: '16px',
                            marginBottom: '12px', borderLeft: '3px solid #d4af37',
                        }}>
                            <p style={{ color: '#f1f5f9', margin: '0 0 4px', fontSize: '14px' }}>
                                💡 "<strong>{r.product}</strong>" from {r.vendor_name}
                            </p>
                            <p style={{ color: '#94a3b8', margin: '4px 0', fontSize: '13px' }}>
                                Suitable for: <span style={{ color: '#d4af37' }}>{r.use_case}</span>
                            </p>
                            {r.reason && (
                                <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '12px', fontStyle: 'italic' }}>
                                    Because: {r.reason.substring(0, 200)}...
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Section 3: Gaps */}
            {report?.gaps?.length > 0 && (
                <div style={{
                    background: 'rgba(30, 41, 59, 0.6)', borderRadius: '12px', padding: '24px',
                    marginBottom: '20px', border: '1px solid rgba(239,68,68,0.2)',
                }}>
                    <h3 style={{ color: '#ef4444', margin: '0 0 16px', fontSize: '15px' }}>⚠️ Portfolio Gaps</h3>
                    {report.gaps.map((g, i) => (
                        <div key={i} style={{
                            background: 'rgba(239,68,68,0.05)', borderRadius: '10px', padding: '16px',
                            marginBottom: '12px', borderLeft: '3px solid #ef4444',
                        }}>
                            <p style={{ color: '#f1f5f9', margin: '0 0 4px', fontSize: '14px' }}>
                                ⚠️ <strong>{g.solution_type}</strong>
                            </p>
                            {g.tender_evidence && (
                                <p style={{ color: '#94a3b8', margin: '4px 0', fontSize: '13px' }}>
                                    Evidence: {g.tender_evidence.substring(0, 200)}
                                </p>
                            )}
                            {g.suggested_vendors && (
                                <p style={{ color: '#d4af37', margin: '4px 0 0', fontSize: '12px' }}>
                                    Consider: {g.suggested_vendors}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Section 4: Winning Combinations */}
            {report?.combinations?.length > 0 && (
                <div style={{
                    background: 'rgba(30, 41, 59, 0.6)', borderRadius: '12px', padding: '24px',
                    border: '1px solid rgba(34,197,94,0.2)',
                }}>
                    <h3 style={{ color: '#22c55e', margin: '0 0 16px', fontSize: '15px' }}>🔗 Winning Combinations</h3>
                    {report.combinations.map((c, i) => (
                        <div key={i} style={{
                            background: 'rgba(34,197,94,0.05)', borderRadius: '10px', padding: '16px',
                            marginBottom: '12px', borderLeft: '3px solid #22c55e',
                        }}>
                            <p style={{ color: '#d4af37', margin: '0 0 8px', fontSize: '14px', fontWeight: 600 }}>
                                🔗 {c.use_case}
                            </p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {c.products?.map((p, j) => (
                                    <span key={j} style={{
                                        background: 'rgba(34,197,94,0.15)', color: '#22c55e',
                                        padding: '4px 10px', borderRadius: '6px', fontSize: '12px',
                                    }}>{p}</span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!report?.coverage && !report?.recommendations && !report?.gaps && !report?.combinations && (
                <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                    <p>No report data available. Click Regenerate to create a new report.</p>
                </div>
            )}
        </div>
    )
}